import LogRocket from 'logrocket';
import { type Dispatch, type SetStateAction, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { type AppReady, MAX_3RD_PARTY_WAIT_TIME } from '..';

type LogRocketContextType = {
  logRocket?: typeof LogRocket;
};

export const LogRocketContext = createContext<LogRocketContextType>({});

export function useLogRocket() {
  const logRocket = useContext(LogRocketContext);
  return logRocket;
}

export async function initLogRocket(appKey: string) {
  // eslint-disable-next-line no-return-await
  return await new Promise<void>((resolve) => {
    try {
      if (import.meta.env.PROD) {
        // POS-19460: auth endpoints leak credentials in both request and response bodies, so both
        // must be stripped. IResponse has no `url` field, so requestSanitizer tracks credential-bearing
        // reqIds and responseSanitizer consumes them to decide what to strip.
        const isCredentialBearingUrl = (url: string) => {
          const lowered = url.toLowerCase();
          return (
            lowered.includes('/posv3/user/employeelogin') ||
            lowered.includes('/posv3/user/resetpassword') ||
            lowered.includes('/curbside/login')
          );
        };
        const redactSessionId = (body?: string) => {
          if (!body) {
            return body;
          }

          try {
            const parsedBody: unknown = JSON.parse(body);
            if (!parsedBody || typeof parsedBody !== 'object' || Array.isArray(parsedBody)) {
              return body;
            }

            const entries = Object.entries(parsedBody);
            if (!entries.some(([key]) => key.toLowerCase() === 'sessionid')) {
              return body;
            }

            return JSON.stringify(Object.fromEntries(entries.filter(([key]) => key.toLowerCase() !== 'sessionid')));
          } catch {
            return body;
          }
        };

        // POS-19460 review: a count-based FIFO cap could evict a still-in-flight request just because
        // many others were concurrently outstanding, causing its later response to be recorded
        // unredacted (an open-fail on lost correlation). Track by TTL instead: an auth request that
        // hasn't resolved within CREDENTIAL_TRACKING_TTL_MS is treated as abandoned, but a live request
        // is never evicted purely due to concurrency volume -- so correlation never silently fails.
        const CREDENTIAL_TRACKING_TTL_MS = 60_000;
        const credentialBearingRequestExpiry = new Map<string, number>();
        const sweepExpiredCredentialRequests = () => {
          const now = Date.now();
          for (const [reqId, expiresAt] of credentialBearingRequestExpiry) {
            if (expiresAt <= now) {
              credentialBearingRequestExpiry.delete(reqId);
            }
          }
        };
        const trackCredentialBearingRequest = (reqId: string) => {
          sweepExpiredCredentialRequests();
          credentialBearingRequestExpiry.set(reqId, Date.now() + CREDENTIAL_TRACKING_TTL_MS);
        };
        const consumeCredentialBearingRequest = (reqId: string) => {
          sweepExpiredCredentialRequests();
          return credentialBearingRequestExpiry.delete(reqId);
        };

        LogRocket.init(appKey, {
          network: {
            requestSanitizer: (request) => {
              if (isCredentialBearingUrl(request.url)) {
                request.body = undefined;
                trackCredentialBearingRequest(request.reqId);
              } else {
                request.body = redactSessionId(request.body);
              }

              return request;
            },
            responseSanitizer: (response) => {
              if (consumeCredentialBearingRequest(response.reqId)) {
                response.body = undefined;
              }

              return response;
            },
          },
        });
        resolve();
      } else if (import.meta.env.DEV) {
        resolve();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize LogRocket', error);

      // We will still load the page if LogRocket fails to initialize
      resolve();
    }
  });
}

export type LogRocketProviderProps = {
  appKey: string;
  children: React.ReactNode;
  setReady: Dispatch<SetStateAction<AppReady>>;
  ready: AppReady;
};

export function LogRocketProvider({ children, appKey, setReady, ready }: LogRocketProviderProps) {
  const [logRocketReady, setLogRocketReady] = useState(false);
  const [logRocketForceReady, setLogRocketForceReady] = useState(false);
  // TODO: something like this..
  // const { data: user } = useUser();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLogRocketForceReady(true);
    }, MAX_3RD_PARTY_WAIT_TIME);

    async function init() {
      await initLogRocket(appKey);
      setLogRocketReady(true);
    }
    void init();

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Check that the logrocket ready state is not already set so the screen doesn't blip after the timeout
    if (!ready.logrocket && (logRocketReady || logRocketForceReady)) {
      // eslint-disable-next-line no-console
      // TODO: something like this..
      // LogRocket.identify(action.data.user.Id, {
      //   ...action.data.user,
      //   displayName: action.data.user.FullName,
      //   email: action.data.user.UserName,
      // });
      setReady((prev) => ({
        ...prev,
        logrocket: true,
      }));
    }
  }, [ready.logrocket, logRocketReady, logRocketForceReady]);

  const value = useMemo(() => ({ logRocket: LogRocket }), [logRocketReady]);

  if (!logRocketReady && !logRocketForceReady) {
    return null;
  }

  return <LogRocketContext.Provider value={value}>{children}</LogRocketContext.Provider>;
}
